import dayjs, { Dayjs } from 'dayjs';
import React from 'react';

import cx from '../../utils/classnames';

import styles from './calendarBody.module.scss';

export type SelectedState =
  | 'selected'
  | 'selectedStartDate'
  | 'selectedEndDate'
  | 'hoveredStartDate'
  | 'hoveredEndDate'
  | 'inSelectedRange'
  | 'unSelected';

export type DayState = 'outDated' | 'invalidMonth' | 'weekend' | 'default';

type Props = {
  /** Custom style for wrapper */
  className?: string;
  /** Date to display */
  day: Dayjs;
  /** String to define component date state */
  dayState?: DayState;
  /** Custom style for day item */
  itemClassName?: string;
  /** Date format */
  format?: string;
  /** callback function when item is clicked */
  onClick?: (event: React.MouseEvent, day: Dayjs) => void;
  /** callback function when item is being hovered */
  onHover?: (event: React.MouseEvent, day?: Dayjs) => void;
  /** String to define component highlight state */
  selectedState: SelectedState;
  /** Flag to highlight the current date */
  shouldShowCurrentDate?: boolean;
  /** Extra component underneath calendar per day */
  extraComponent?: JSX.Element;
  /** Date Selection Highlight type (default circle) */
  highlightType?: 'circle' | 'full';
};

function CalendarDay(props: Props) {
  const {
    className,
    day,
    dayState = 'default',
    itemClassName,
    format = 'D',
    onClick,
    onHover,
    selectedState = 'unSelected',
    shouldShowCurrentDate = false,
    extraComponent,
    highlightType = 'circle',
    ...rest
  } = props;

  const displayDate = day.format(format);
  const onClickItem = (event: React.MouseEvent) => onClick?.(event, day);
  const onMouseOverItem = (event: React.MouseEvent) => onHover && onHover(event, day);
  const onMouseLeaveItem = (event: React.MouseEvent) => onHover && onHover(event, undefined);
  const isCurrentDate = dayjs().isSame(dayjs(day), 'day');

  const shouldSelectionHighlightEntireDay = highlightType === 'full';

  return (
    <td
      className={cx(
        styles.itemWrapper,
        styles[selectedState],
        styles[dayState],
        className,
        shouldSelectionHighlightEntireDay && styles[selectedState],
      )}
      onClick={onClickItem}
      onMouseOver={onMouseOverItem}
      onMouseLeave={onMouseLeaveItem}
      data-testid={`calendarDay-td-${dayState}`}
      {...rest}
    >
      <div className={cx(!shouldSelectionHighlightEntireDay && styles[selectedState])}>
        <div
          className={cx(
            styles.item,
            itemClassName,
            shouldShowCurrentDate && !shouldSelectionHighlightEntireDay && isCurrentDate
              ? styles.today
              : null,
          )}
          data-testid={
            (dayState === 'default' || dayState === 'weekend') && 'calendarDay-selectable-day'
          }
        >
          {displayDate}
        </div>
      </div>
      {!!extraComponent && extraComponent}
    </td>
  );
}

export default React.memo(CalendarDay);
